import type { FormProps, FormData } from '@/components/form';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Input from '@/components/input';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { cn } from '@/utils/helper';
import v1 from '@/components/form/form-v1.module.css';
import v2 from '@/components/form/form-v2.module.css';
import Control from '@/components/control';

const initiateForm = (schema: FormProps['schema']): FormData => {
  const initialForm: FormData = {};
  schema.forEach((row) => {
    row.forEach((cell) => {
      initialForm[cell.key] = '';
    });
  });
  return initialForm;
};

const Form = (props: FormProps) => {
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const [form, setForm] = useState(initiateForm(props.schema));

  const debouncedFormUpdate = useDebouncedCallback((value) => {
    props.onChange(props.name, value);
  }, 1000);

  const onFieldChange = (key: string, value: string) => {
    setForm((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    debouncedFormUpdate(form);
  }, [form]);

  return (
    <div className={cn(v1.Form, v2.Form)}>
      {props.schema.map((row, rowIndex) => (
        <div key={`form-row-${rowIndex}`} className={cn(v1.Row, v2.Row)}>
          {row.map((cell, cellIndex) => {
            switch (cell.type) {
              case 'text':
              case 'number':
              case 'email':
                return (
                  <div key={`form-cell-${rowIndex}-${cellIndex}`} style={{ flexBasis: `${cell.width}%` }}>
                    {cssVersion === 1 ? (
                      <Input
                        ui='outlined'
                        type={cell.type}
                        label={cell.placeholder}
                        value={form[cell.key] || ''}
                        placeholder={cell.placeholder}
                        name={cell.key}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onFieldChange(cell.key, e.target.value)}
                      />
                    ) : (
                      <Control.Text
                        label={cell.placeholder}
                        id={cell.key}
                        value={form[cell.key] || ''}
                        onChange={(value, id) => onFieldChange(id, value)}
                      />
                    )}
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>
      ))}
    </div>
  );
};

export default Form;
